import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Barcode, Trash2, Package } from "lucide-react";
import { toast } from "sonner";

/**
 * Repuestos utilizados en una MP/MC: el técnico PISTOLEA el código de barras
 * (los lectores escriben el código y envían Enter) o busca por nombre/SKU en
 * el desplegable. Cada consumo descuenta su inventario al instante y queda
 * registrado con el folio del informe (reversible con el basurero).
 *
 * Props: origenTipo ("mp"|"mc"), origenFolio (folio del informe).
 */
export default function ConsumoRepuestos({ origenTipo, origenFolio }) {
  const { api } = useAuth();
  const [q, setQ] = useState("");
  const [sugerencias, setSugerencias] = useState([]);
  const [consumos, setConsumos] = useState([]);
  const timer = useRef(null);

  const cargar = useCallback(() => {
    if (!origenFolio) return;
    api.get(`/api/tienda/consumos?folio=${encodeURIComponent(origenFolio)}`)
      .then((r) => setConsumos(r.data)).catch(() => {});
  }, [api, origenFolio]);
  useEffect(() => { cargar(); }, [cargar]);

  const registrar = async (productoId) => {
    try {
      const r = await api.post("/api/tienda/consumo", {
        producto_id: productoId, cantidad: 1,
        origen_tipo: origenTipo, origen_folio: origenFolio || "SIN FOLIO",
      });
      toast.success(`✓ ${r.data.nombre} descontado de tu inventario`);
      setQ(""); setSugerencias([]); cargar();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al registrar"); }
  };

  const buscar = async (texto, esEnter) => {
    if (!texto.trim()) { setSugerencias([]); return; }
    try {
      const r = await api.get(`/api/tienda/buscar?q=${encodeURIComponent(texto.trim())}`);
      // pistoleo: match exacto por código de barras/SKU → registrar directo
      if (r.data.length === 1 && r.data[0].exacto && esEnter) {
        registrar(r.data[0].id);
        return;
      }
      setSugerencias(r.data);
      if (esEnter && r.data.length === 0) toast.error("Producto no encontrado");
    } catch { /* silencioso */ }
  };

  const onChange = (v) => {
    setQ(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => buscar(v, false), 300);
  };

  const eliminar = async (c) => {
    try {
      await api.delete(`/api/tienda/consumos/${c.id}`);
      toast.success("Consumo revertido (stock repuesto)");
      cargar();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-5 space-y-3">
        <h3 className="font-semibold text-gray-800 flex items-center gap-2">
          <Package className="h-4 w-4 text-[#E0653A]" /> Repuestos utilizados
          <span className="text-xs font-normal text-gray-400">(descuenta tu inventario)</span>
        </h3>
        <div className="relative">
          <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input value={q} onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); clearTimeout(timer.current); buscar(q, true); } }}
            placeholder="Pistolea el código de barras o busca por nombre/SKU…"
            className="pl-9 h-11 bg-white no-uppercase" />
          {sugerencias.length > 0 && (
            <div className="absolute z-20 inset-x-0 top-full mt-1 rounded-xl border bg-white shadow-lg max-h-56 overflow-y-auto">
              {sugerencias.map((s) => (
                <button key={s.id} type="button" onClick={() => registrar(s.id)}
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#0FB4B7]/5 border-b last:border-0">
                  {s.nombre} <span className="text-xs text-gray-400">{s.sku}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {consumos.length > 0 && (
          <div className="space-y-1.5">
            {consumos.map((c) => (
              <div key={c.id} className="flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2">
                <span className="flex-1 text-sm text-gray-700 truncate">
                  {c.nombre} <span className="text-xs text-gray-400">×{c.cantidad}{c.sku ? ` · ${c.sku}` : ""}</span>
                </span>
                <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                  onClick={() => eliminar(c)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
